import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  useMediaQuery
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { formatCurrency } from 'src/utils';
import useResolution from 'src/hooks/useResolution';

export interface RevenueSummaryCardProps {
  title?: string;
  currentValue?: number;
  previousValue?: number;
  isPercentage?: boolean;
  isCount?: boolean;
  isGrowth?: boolean;
}

interface StyleProps {
  isMobile?: boolean;
  stackChips?: boolean;
}

const useStyles = makeStyles((theme) => ({
  card: (props: StyleProps) => ({
    width: '100%',
    minHeight: props.isMobile ? 211 : 188,
    boxSizing: 'border-box',
    display: 'flex'
  }),
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: theme.spacing(1.5),
    padding: 5
  },
  valueColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  chipsRow: (props: StyleProps) => ({
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    flexWrap: 'wrap',
    flexDirection: props.stackChips ? 'column' : 'row'
  }),
  positive: {
    backgroundColor: '#6E8F7A',
    color: theme.palette.common.white
  },
  negative: {
    backgroundColor: '#BF6B72',
    color: theme.palette.common.white
  },
  outlinedChip: {
    borderColor: 'currentColor'
  }
}));

export const MetricCard: React.FC<RevenueSummaryCardProps> = ({
  title,
  currentValue,
  previousValue,
  isPercentage = false,
  isCount = false,
  isGrowth = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { md, xs } = useResolution();
  const stackChips = md && !xs;
  const classes = useStyles({ isMobile, stackChips });

  const difference = (currentValue || 0) - (previousValue || 0);

  const percentChange =
    previousValue === 0 ? 0 : (difference / (previousValue || 0)) * 100;

  const formatValue = (value: number) =>
    isPercentage
      ? `${value.toFixed(2)}%`
      : isCount
      ? value
      : formatCurrency(value);

  const isPositive = difference >= 0;

  return (
    <Card elevation={1} className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Typography variant="subtitle2" color="textSecondary">
          {title}
        </Typography>

        <Typography
          variant="h5"
          style={{
            color: isGrowth
              ? (currentValue || 0) >= 0
                ? '#2e7d32'
                : '#bf5860ff'
              : 'inherit'
          }}
        >
          {formatValue(currentValue || 0)}
        </Typography>

        {!isGrowth && (
          <>
            <div className={classes.valueColumn}>
              <div className={classes.chipsRow}>
                {!isPercentage && (
                  <Chip
                    label={`${isPositive ? '▲' : '▼'} ${Math.abs(
                      percentChange
                    ).toFixed(1)}%`}
                    size="small"
                    className={isPositive ? classes.positive : classes.negative}
                  />
                )}
                <Chip
                  label={`${
                    isPercentage ? (isPositive ? '▲' : '▼') : ''
                  } ${formatValue(Math.abs(difference))}`}
                  size="small"
                  variant="outlined"
                  className={classes.outlinedChip}
                  style={{
                    color: isPositive ? '#2e7d32' : '#d32f2f'
                  }}
                />
              </div>
            </div>

            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                vs last month
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatValue(previousValue || 0)}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
